// Categorical color palette for timeline activity types — the 5 hues that
// will eventually drive the activity-marker dots on every card (Phase 7a)
// and that already drive the filter-bar identity colors here.
//
// Canon: everself-design-system → 04-surfaces/orbit-and-internal/
// activity-timeline.mdx → "Activity-type → color mapping (5 groups)".
// Tokens: --ev-color-cat-{sage,mustard,dusty-rose,slate-blue,lavender}.
//
// Each category groups a small set of activity types so a clinician scans
// 5 colors, not 11+. Mapping rationale lives in the canon.

export const ACTIVITY_CATEGORY_COLORS = {
  conversations: '#679773', // Sage — direct patient touchpoints (SMS / chat / call)
  notes: '#C28B2C', // Mustard — RD / chart / provider notes / annotations
  ordersResults: '#B8506A', // Dusty Rose — Rx / lab orders / lab results / referrals
  appointments: '#3180E3', // Slate Blue — calendar / scheduled / completed / future
  careCoordination: '#7E6CB8', // Lavender — fax / pharmacy / partner / specialist comms
  // Teal — payment orders and the money that moves against them. The sixth
  // group: activity-timeline.mdx maps five and says "never invent a sixth", and
  // 06-intake-project/ci-orbit-payment-flow.mdx is the amendment that adds this
  // one. Teal was already a sanctioned categorical hue (--ev-color-cat-teal).
  billing: '#3D8B8B',
} as const;

export type ActivityCategory = keyof typeof ACTIVITY_CATEGORY_COLORS;

/**
 * Hex → `rgba(...)` with given alpha. Used for tinted filter-badge
 * backgrounds on active state (0.12) and hover (0.08).
 */
export function withAlpha(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Warm White — the 2px ring that breaks the timeline's spine line behind a marker. */
export const TIMELINE_MARKER_RING_COLOR = '#FAFCF7';

/**
 * Activity-timeline canon for a marker: 24px filled circle, Warm White 2px
 * ring, 14px glyph. Shared by the timeline gutter markers and the inbox row
 * icons so the two surfaces can't drift.
 */
export const TIMELINE_MARKER_PROPS = {
  variant: 'filled' as const,
  radius: 'xl' as const,
  size: 24,
  styles: { root: { boxShadow: `0 0 0 2px ${TIMELINE_MARKER_RING_COLOR}` } },
};
